import { useState, useEffect } from 'react';

interface CalendarEvent {
  id: string;
  day: number;
  time: string;
  title: string;
  location: string;
  zoomLink?: string;
}

export default function Calendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<number | null>(new Date().getDate());
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  const [formData, setFormData] = useState({
    time: '',
    title: '',
    location: '',
    zoomLink: ''
  });
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcodeError, setPasscodeError] = useState('');

  // Load events from localStorage
  useEffect(() => {
    // Clear any existing events and start fresh
    localStorage.removeItem('calendarEvents');
    setEvents([]);
  }, []);

  // Save events to localStorage
  const saveEvents = (newEvents: CalendarEvent[]) => {
    setEvents(newEvents);
    localStorage.setItem('calendarEvents', JSON.stringify(newEvents));
  };

  const monthNames = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const today = new Date();
  const isCurrentMonth = currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getEventsForDate = (day: number) => {
    return events.filter(event => event.day === day);
  };

  const handleAddEvent = () => {
    if (!selectedDate) return;
    if (!isAuthenticated) {
      setShowPasscodeModal(true);
      return;
    }
    setShowEventForm(true);
    setEditingEvent(null);
    setFormData({ time: '', title: '', location: '', zoomLink: '' });
  };

  const handleEditEvent = (event: CalendarEvent) => {
    if (!isAuthenticated) {
      setShowPasscodeModal(true);
      return;
    }
    setEditingEvent(event);
    setFormData({
      time: event.time,
      title: event.title,
      location: event.location,
      zoomLink: event.zoomLink || ''
    });
    setShowEventForm(true);
  };

  const handleDeleteEvent = (eventId: string) => {
    if (!isAuthenticated) {
      setShowPasscodeModal(true);
      return;
    }
    if (window.confirm('Are you sure you want to delete this event?')) {
      const newEvents = events.filter(e => e.id !== eventId);
      saveEvents(newEvents);
    }
  };

  const handlePasscodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passcode === '624907') {
      setIsAuthenticated(true);
      setShowPasscodeModal(false);
      setPasscode('');
      setPasscodeError('');
    } else {
      setPasscodeError('Incorrect passcode. Please try again.');
      setPasscode('');
    }
  };

  const handleSubmitEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDate) return;

    if (editingEvent) {
      // Update existing event
      const newEvents = events.map(e => 
        e.id === editingEvent.id 
          ? { ...e, ...formData, day: selectedDate }
          : e
      );
      saveEvents(newEvents);
    } else {
      // Add new event
      const newEvent: CalendarEvent = {
        id: Date.now().toString(),
        day: selectedDate,
        ...formData
      };
      saveEvents([...events, newEvent]);
    }

    setShowEventForm(false);
    setFormData({ time: '', title: '', location: '', zoomLink: '' });
    setEditingEvent(null);
  };

  const selectedEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <section id="calendar" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-16">
          <div className="w-16 h-1 bg-[#D4A574] mb-4"></div>
          <h2 className="text-5xl lg:text-6xl font-bold text-[#2C1810] font-['Space_Mono'] tracking-tight">
            EVENT CALENDAR
          </h2>
        </div>

        <div className="border-4 border-[#2C1810] bg-[#F5E6D3] p-8 lg:p-12">
          <div className="flex items-center justify-between mb-8 pb-6 border-b-4 border-[#2C1810]">
            <button 
              onClick={previousMonth}
              className="w-12 h-12 border-2 border-[#2C1810] bg-white hover:bg-[#D4A574] transition-colors cursor-pointer flex items-center justify-center"
            >
              <i className="ri-arrow-left-s-line text-2xl text-[#2C1810]"></i>
            </button>
            <h3 className="text-3xl font-bold text-[#2C1810] font-['Space_Mono'] tracking-tight">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button 
              onClick={nextMonth}
              className="w-12 h-12 border-2 border-[#2C1810] bg-white hover:bg-[#D4A574] transition-colors cursor-pointer flex items-center justify-center"
            >
              <i className="ri-arrow-right-s-line text-2xl text-[#2C1810]"></i>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-2 mb-4">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-['Space_Mono'] text-[#2C1810] font-bold py-3 bg-[#D4A574] tracking-wider">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: firstDay }).map((_, index) => (
              <div key={`empty-${index}`} className="aspect-square"></div>
            ))}
            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              const dayEvents = getEventsForDate(day);
              const isToday = isCurrentMonth && day === today.getDate();
              const isSelected = day === selectedDate;

              return (
                <button
                  key={day}
                  onClick={() => setSelectedDate(day)}
                  className={`aspect-square border-2 flex flex-col items-center justify-center relative transition-all cursor-pointer font-['Space_Mono'] ${
                    isToday ? 'bg-[#2C1810] border-[#2C1810] text-[#F5E6D3] font-bold' : 
                    isSelected ? 'bg-[#D4A574] border-[#2C1810] text-[#2C1810] font-bold' :
                    'bg-white border-[#2C1810] hover:bg-[#D4A574] text-[#2C1810]'
                  }`}
                >
                  <span className="text-sm">{day}</span>
                  {dayEvents.length > 0 && (
                    <div className="flex gap-1 mt-1">
                      {dayEvents.slice(0, 3).map((event, idx) => (
                        <div 
                          key={idx}
                          className={`w-1.5 h-1.5 ${
                            isToday ? 'bg-[#D4A574]' : isSelected ? 'bg-[#2C1810]' : 'bg-[#D4A574]'
                          }`}
                        ></div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selectedDate && (
            <div className="mt-8 pt-8 border-t-4 border-[#2C1810]">
              <div className="flex justify-between items-center mb-6">
                <h4 className="text-xl font-bold text-[#2C1810] font-['Space_Mono'] tracking-tight">
                  EVENTS ON {monthNames[currentDate.getMonth()]} {selectedDate}
                </h4>
                <button
                  onClick={handleAddEvent}
                  className="px-6 py-2 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] text-sm hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
                >
                  + ADD EVENT
                </button>
              </div>

              {showEventForm && (
                <div className="mb-6 p-6 bg-white border-4 border-[#2C1810]">
                  <h5 className="text-lg font-bold text-[#2C1810] mb-4 font-['Space_Mono']">
                    {editingEvent ? 'EDIT EVENT' : 'NEW EVENT'}
                  </h5>
                  <form onSubmit={handleSubmitEvent} className="space-y-4">
                    <div>
                      <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2">
                        TIME *
                      </label>
                      <input
                        type="text"
                        value={formData.time}
                        onChange={(e) => setFormData({...formData, time: e.target.value})}
                        placeholder="e.g., 10:00 AM"
                        className="w-full px-4 py-2 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2">
                        TITLE *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder="Event title"
                        className="w-full px-4 py-2 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2">
                        LOCATION *
                      </label>
                      <input
                        type="text"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                        placeholder="Event location"
                        className="w-full px-4 py-2 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-['Space_Mono'] text-[#2C1810] mb-2">
                        ZOOM LINK (Optional)
                      </label>
                      <input
                        type="url"
                        value={formData.zoomLink}
                        onChange={(e) => setFormData({...formData, zoomLink: e.target.value})}
                        placeholder="https://zoom.us/j/..."
                        className="w-full px-4 py-2 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                      />
                    </div>
                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex-1 py-2 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] text-sm hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
                      >
                        {editingEvent ? 'UPDATE' : 'SAVE'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowEventForm(false);
                          setEditingEvent(null);
                          setFormData({ time: '', title: '', location: '', zoomLink: '' });
                        }}
                        className="px-6 py-2 border-2 border-[#2C1810] text-[#2C1810] font-bold font-['Space_Mono'] text-sm hover:bg-[#2C1810] hover:text-[#F5E6D3] transition-all cursor-pointer whitespace-nowrap"
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {selectedEvents.length > 0 ? (
                <div className="space-y-4">
                  {selectedEvents.map((event) => (
                    <div 
                      key={event.id}
                      className="flex items-start gap-4 p-6 bg-white border-4 border-[#2C1810] hover:border-[#D4A574] transition-all"
                    >
                      <div className="flex-shrink-0">
                        <span className="inline-block px-4 py-2 bg-[#2C1810] text-[#F5E6D3] text-xs font-['Space_Mono'] tracking-wider whitespace-nowrap">
                          {event.time}
                        </span>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-bold text-[#2C1810] mb-2 font-['Space_Mono'] text-sm">{event.title}</h5>
                        <p className="text-xs text-[#5C4033] flex items-center gap-2 font-['DM_Sans'] mb-2">
                          <i className="ri-map-pin-line"></i>
                          {event.location}
                        </p>
                        {event.zoomLink && (
                          <a 
                            href={event.zoomLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-xs text-[#D4A574] hover:text-[#2C1810] font-['Space_Mono'] transition-colors cursor-pointer"
                          >
                            <i className="ri-video-line"></i>
                            JOIN ZOOM MEETING
                          </a>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditEvent(event)}
                          className="w-8 h-8 border-2 border-[#2C1810] text-[#2C1810] hover:bg-[#D4A574] transition-all cursor-pointer flex items-center justify-center"
                          title="Edit event"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteEvent(event.id)}
                          className="w-8 h-8 border-2 border-[#2C1810] text-[#2C1810] hover:bg-[#2C1810] hover:text-[#F5E6D3] transition-all cursor-pointer flex items-center justify-center"
                          title="Delete event"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-[#5C4033] font-['DM_Sans'] text-sm">
                  No events scheduled for this day. Click "ADD EVENT" to create one.
                </div>
              )}
            </div>
          )}

          <div className="mt-8 pt-6 border-t-2 border-[#D4A574]">
            <p className="text-xs text-[#5C4033] font-['DM_Sans']">
              {isAuthenticated ? (
                <>🔓 You are authorized to manage events. Events are currently stored in your browser. For permanent storage and access from any device, connect to Supabase.</>
              ) : (
                <>🔒 Passcode required to add, edit, or delete events. Events are currently stored in your browser. For permanent storage and access from any device, connect to Supabase.</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Passcode Modal */}
      {showPasscodeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-6">
          <div className="bg-white border-4 border-[#2C1810] p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-[#2C1810] mb-4 font-['Space_Mono']">
              ENTER PASSCODE
            </h3>
            <p className="text-sm text-[#5C4033] mb-6 font-['DM_Sans']">
              Please enter the passcode to manage calendar events.
            </p>
            <form onSubmit={handlePasscodeSubmit} className="space-y-4">
              <div>
                <input
                  type="password"
                  value={passcode}
                  onChange={(e) => {
                    setPasscode(e.target.value);
                    setPasscodeError('');
                  }}
                  placeholder="Enter passcode"
                  className="w-full px-4 py-3 border-2 border-[#2C1810] focus:outline-none focus:border-[#D4A574] font-['DM_Sans'] text-sm"
                  autoFocus
                />
                {passcodeError && (
                  <p className="text-xs text-red-600 mt-2 font-['DM_Sans']">
                    {passcodeError}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 py-3 bg-[#2C1810] text-[#F5E6D3] font-bold font-['Space_Mono'] text-sm hover:bg-[#D4A574] hover:text-[#2C1810] transition-all cursor-pointer whitespace-nowrap"
                >
                  SUBMIT
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasscodeModal(false);
                    setPasscode('');
                    setPasscodeError('');
                  }}
                  className="px-6 py-3 border-2 border-[#2C1810] text-[#2C1810] font-bold font-['Space_Mono'] text-sm hover:bg-[#2C1810] hover:text-[#F5E6D3] transition-all cursor-pointer whitespace-nowrap"
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
